import { Controller } from '@hotwired/stimulus'

type EventModifierType = 'prevent' | 'stop' | 'stopImmediate'
type EventModifierDetail = {
  eventName: string
  modifiers: EventModifierType[]
  phase: 'capture' | 'bubble'
}

export default class EventModController extends Controller {
  declare readonly preventValue: string
  declare readonly stopValue: string
  declare readonly stopImmediateValue: string
  declare readonly hasPreventValue: boolean
  declare readonly hasStopValue: boolean
  declare readonly hasStopImmediateValue: boolean

  static values = {
    prevent: String,
    stop: String,
    stopImmediate: String,
  }

  modifierDetails: EventModifierDetail[]

  connect() {
    if (this.#hasAnyModifier()) {
      this.#generateModifiersDetail()
      this.#handleEvents()
    }
  }

  disconnect(): void {
    if (this.#hasAnyModifier()) {
      this.#handleEvents('remove')
    }
  }

  #generateModifiersDetail() {
    const preventEvents = this.preventValue.split(',')
    const stopEvents = this.stopValue.split(',')
    const stopImmediateEvents = this.stopImmediateValue.split(',')
    const uniqueEvents = new Set(preventEvents.concat(...stopEvents, ...stopImmediateEvents))
    this.modifierDetails = Array.from(uniqueEvents).reduce((acc, event) => {
      if (!event) return acc

      const mods = []
      preventEvents.includes(event) && mods.push('prevent')
      stopEvents.includes(event) && mods.push('stop')
      stopImmediateEvents.includes(event) && mods.push('stopImmediate')
      const [eventName, phase = 'bubble'] = event.split(':')
      return acc.concat([
        {
          eventName,
          modifiers: mods,
          phase,
        },
      ])
    }, [])
  }

  #hasAnyModifier() {
    return this.hasPreventValue || this.hasStopValue || this.hasStopImmediateValue
  }

  #handleEvents(operation = 'add') {
    const fullOpName = `${operation}EventListener`
    this.modifierDetails.forEach(modifierEvent => {
      this.#handleEvent(modifierEvent.eventName, modifierEvent.modifiers, fullOpName, modifierEvent.phase)
    })
  }

  #handleEvent(eventName: string, types: EventModifierType[], operation: string, phase = 'bubble') {
    const options = phase === 'capture' ? { capture: true } : {}
    if (!types.length) {
      console.warn(`EventModifiersController: No Modifier for event ${eventName}. Skipping`)
    }
    this.element[operation].call(
      this.element,
      eventName,
      event => {
        types.forEach(type => {
          this.#handleModifier(event, type)
          return
        })
      },
      options,
    )
  }

  #handleModifier(event: Event, type: EventModifierType) {
    switch (type) {
      case 'prevent':
        event.preventDefault()
        break
      case 'stop':
        event.stopPropagation()
        break
      case 'stopImmediate':
        event.stopImmediatePropagation()
        break
      default:
        console.warn(`EventModifiers: modifier type ${type} not recognized. Skipping`)
    }
  }
}
